import { useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import type React from 'react';

import type * as api from '@gitbook/api-client';
import {
    Heading,
    LabeledContainer,
    LabeledContainerActionableTitle,
    LabeledContainerBody,
    LabeledContainerHeader,
    usePanelTransitionStyle,
} from '@gitbook/spine-react';
import { hasMandateProp } from '@gitbook/util-ts';

import { apiQuery } from '../../../state';
import { UserAvatarStack } from '../../Users';
import { ChangeRequestParticipantsList } from '../ChangeRequestParticipantsList';
import { ChangeRequestReviewersBadges } from '../ChangeRequestReviewersBadges';
import { ChangeRequestReviewersList } from '../ChangeRequestReviewersList';
import { ChangeRequestTopicLink } from '../ChangeRequestTopicLink';
import { useChangeRequestSidePanelCollapsed } from './ChangeRequestSidePanel';

type ChangeRequestDetailsProps = {
    changeRequest: api.ChangeRequest;
    space: api.Space;
};

export function ChangeRequestDetails(props: ChangeRequestDetailsProps) {
    const { changeRequest, space } = props;
    const transitionStyle = usePanelTransitionStyle();
    const isCollapsed = useChangeRequestSidePanelCollapsed();

    return (
        <div
            style={{ ...transitionStyle, transitionProperty: 'opacity, margin-right' }}
            className={clsx(
                '-mr-[19rem] w-[19rem] divide-y border-l opacity-30',
                isCollapsed ? null : '@4xl:mr-0 @4xl:opacity-100'
            )}
        >
            {changeRequest.status !== 'draft' ? (
                <SidebarSection>
                    <ChangeRequestReviewersList changeRequest={changeRequest} space={space} />
                </SidebarSection>
            ) : null}
            <SidebarSection>
                <ChangeRequestParticipantsList changeRequest={changeRequest} />
            </SidebarSection>
        </div>
    );
}

export function ChangeRequestDetailsResponsive(props: ChangeRequestDetailsProps) {
    const { changeRequest, space } = props;
    const isCollapsed = useChangeRequestSidePanelCollapsed();
    const transitionStyle = usePanelTransitionStyle();

    return (
        <div
            style={{
                transitionProperty: 'opacity, height, transform',
                transitionBehavior: 'allow-discrete',
                ...transitionStyle,
            }}
            className={clsx(
                'h-auto origin-top-left overflow-hidden',
                isCollapsed ? null : '@4xl:h-0 @4xl:scale-75 @4xl:opacity-0'
            )}
        >
            <div className="flex flex-row flex-wrap gap-x-6 gap-y-3 pb-4">
                <ChangeRequestReviewersBadges changeRequest={changeRequest} space={space} />
                <ChangeRequestParticipantsResponsive changeRequest={changeRequest} />
                {hasMandateProp(changeRequest, 'conversationsIssuesCluster') ? (
                    <ChangeRequestTopicLink changeRequest={changeRequest} space={space} />
                ) : null}
            </div>
        </div>
    );
}

function ChangeRequestParticipantsResponsive(props: { changeRequest: api.ChangeRequest }) {
    const { changeRequest } = props;

    const { data: edits } = useSuspenseQuery(
        apiQuery('getContributorsByChangeRequestId', {
            spaceId: changeRequest.space,
            changeRequestId: changeRequest.id,
        })
    );

    const users = edits.items.map((item) => item.user);

    return (
        <LabeledContainer level={2}>
            <LabeledContainerHeader>
                <LabeledContainerActionableTitle>
                    <Heading>Participants</Heading>
                </LabeledContainerActionableTitle>
            </LabeledContainerHeader>
            <LabeledContainerBody>
                <UserAvatarStack users={users.map((user) => ({ id: user.id }))} size="sm" />
            </LabeledContainerBody>
        </LabeledContainer>
    );
}

function SidebarSection(props: { children: React.ReactNode }) {
    return <div className="p-4">{props.children}</div>;
}
